import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "EN" },
  { code: "pl", label: "PL" },
  { code: "es", label: "ES" },
  { code: "fr", label: "FR" },
];

export default function LanguageSwitcher({ onDark = false }: { onDark?: boolean }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === i18n.language) ?? languages[0];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function select(lang: string) {
    i18n.changeLanguage(lang);
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors cursor-pointer ${
          onDark
            ? "border-background-50/40 bg-background-50/15 text-background-50 hover:bg-background-50/25"
            : "border-background-300/70 bg-background-50 text-foreground-800 hover:bg-background-100"
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-global-line text-sm"></i>
        </span>
        <span className="whitespace-nowrap">{current.label}</span>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? "rotate-180" : ""}`}></i>
        </span>
      </button>

      {open && (
        <ul
          className="absolute right-0 z-50 mt-2 w-32 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1"
          role="listbox"
        >
          {languages.map((lang) => (
            <li key={lang.code}>
              <button
                type="button"
                onClick={() => select(lang.code)}
                className={`flex w-full items-center justify-between px-4 py-2 text-sm transition-colors cursor-pointer ${
                  lang.code === current.code
                    ? "bg-primary-50 font-semibold text-primary-700"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
                role="option"
                aria-selected={lang.code === current.code}
              >
                <span className="whitespace-nowrap">{lang.label}</span>
                {lang.code === current.code && (
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-check-line text-sm"></i>
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}